import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { useRequest } from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import type { Order } from "~/v2/api/rest/account";
import { getAtdOrder } from "./api";
import { atdReplacementChainQueryKeys } from "./replacementChainQueryKeys";
import {
  buildReplacementHistory,
  type ReplacementChainIssue,
  type ReplacementHistory,
} from "./replacementChains";

/** Maximum replacement-link distance followed from the clicked order. */
export const REPLACEMENT_CHAIN_DEPTH_LIMIT = 20;
/** Additional guard for malformed branching graphs within the depth limit. */
export const REPLACEMENT_CHAIN_REQUEST_LIMIT = 40;

const DETAIL_CACHE_STALE_TIME_MS = 30_000;

const normalizeID = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

interface LoadAtdReplacementChainParams {
  accountID: string;
  product: "live" | "paper";
  selectedOrder: Order;
  fetchOrder: (orderID: string) => Promise<Order | null>;
  depthLimit?: number;
  requestLimit?: number;
}

interface PendingOrder {
  order: Order;
  depth: number;
}

interface TraversalState {
  attemptedIDs: Set<string>;
  limitReported: boolean;
  ordersByID: Map<string, Order>;
  requestCount: number;
  traversalIssues: ReplacementChainIssue[];
}

const reportTraversalLimit = (
  state: TraversalState,
  depthLimit: number,
  orderID: string,
  referencedOrderID: string,
) => {
  if (state.limitReported) {
    return;
  }
  state.limitReported = true;
  state.traversalIssues.push({
    code: "depth-limit",
    orderID,
    referencedOrderID,
    message: `Replacement history stopped at the ${depthLimit}-link safety limit.`,
  });
};

const loadReferencedOrder = async ({
  current,
  depthLimit,
  fetchOrder,
  referencedOrderID,
  requestLimit,
  state,
}: {
  current: PendingOrder;
  depthLimit: number;
  fetchOrder: (orderID: string) => Promise<Order | null>;
  referencedOrderID?: string;
  requestLimit: number;
  state: TraversalState;
}): Promise<PendingOrder | undefined> => {
  if (
    !referencedOrderID ||
    state.ordersByID.has(referencedOrderID) ||
    state.attemptedIDs.has(referencedOrderID)
  ) {
    return undefined;
  }
  const currentID = normalizeID(current.order.id) ?? "";
  if (current.depth >= depthLimit || state.requestCount >= requestLimit) {
    reportTraversalLimit(state, depthLimit, currentID, referencedOrderID);
    return undefined;
  }

  state.attemptedIDs.add(referencedOrderID);
  state.requestCount += 1;
  try {
    const fetchedOrder = await fetchOrder(referencedOrderID);
    if (!fetchedOrder) {
      return undefined;
    }
    const fetchedOrderID = normalizeID(fetchedOrder.id);
    if (fetchedOrderID !== referencedOrderID) {
      state.traversalIssues.push({
        code: "conflicting-link",
        orderID: currentID,
        referencedOrderID,
        message: `Order ${referencedOrderID} returned a different order identity.`,
      });
      return undefined;
    }
    state.ordersByID.set(fetchedOrderID, fetchedOrder);
    return { order: fetchedOrder, depth: current.depth + 1 };
  } catch (error) {
    if (!(axios.isAxiosError(error) && error.response?.status === 404)) {
      state.traversalIssues.push({
        code: "fetch-failed",
        orderID: currentID,
        referencedOrderID,
        message: `Order version ${referencedOrderID} could not be loaded.`,
      });
    }
    return undefined;
  }
};

/**
 * Traverses both pointer directions around one already-loaded detail record.
 * The selected order is never fetched again, and every neighboring ID is
 * requested at most once during this traversal.
 */
export const loadAtdReplacementChain = async ({
  selectedOrder,
  fetchOrder,
  depthLimit = REPLACEMENT_CHAIN_DEPTH_LIMIT,
  requestLimit = REPLACEMENT_CHAIN_REQUEST_LIMIT,
}: LoadAtdReplacementChainParams): Promise<ReplacementHistory> => {
  const selectedOrderID = normalizeID(selectedOrder.id) ?? "";
  const ordersByID = new Map<string, Order>();
  if (selectedOrderID) {
    ordersByID.set(selectedOrderID, selectedOrder);
  }
  const ordersWithoutID = selectedOrderID ? [] : [selectedOrder];
  const pending: PendingOrder[] = [{ order: selectedOrder, depth: 0 }];
  const state: TraversalState = {
    attemptedIDs: new Set<string>(),
    limitReported: false,
    ordersByID,
    requestCount: 0,
    traversalIssues: [],
  };

  while (pending.length > 0) {
    const current = pending.shift();
    if (!current) {
      break;
    }
    const references = [
      normalizeID(current.order.replaces),
      normalizeID(current.order.replaced_by),
    ];

    for (const referencedOrderID of references) {
      const referencedOrder = await loadReferencedOrder({
        current,
        depthLimit,
        fetchOrder,
        referencedOrderID,
        requestLimit,
        state,
      });
      if (referencedOrder) {
        pending.push(referencedOrder);
      }
    }
  }

  return (
    buildReplacementHistory(
      [...ordersByID.values(), ...ordersWithoutID],
      selectedOrderID,
      state.traversalIssues,
    ) ?? {
      versions: [selectedOrder],
      rejectedAttempts: [],
      ambiguousVersions: [],
      headOrder: selectedOrder,
      isHeadKnown: false,
      isRootKnown: false,
      clickedOrderID: selectedOrderID,
      issues: state.traversalIssues,
      isComplete: state.traversalIssues.length === 0,
    }
  );
};

export interface UseAtdReplacementChainParams {
  accountID?: string;
  product?: "live" | "paper";
  orderID?: string;
  selectedOrder?: Order | null;
  enabled: boolean;
}

export const useAtdReplacementChain = ({
  accountID,
  product,
  orderID,
  selectedOrder,
  enabled,
}: UseAtdReplacementChainParams) => {
  const queryClient = useQueryClient();
  const selectedVersion =
    normalizeID(selectedOrder?.updated_at) ??
    normalizeID(selectedOrder?.id) ??
    "";

  return useRequest<ReplacementHistory | null>(
    atdReplacementChainQueryKeys.detail({
      accountID: accountID ?? "",
      product,
      selectedOrderID: orderID ?? "",
      selectedVersion,
    }),
    () => {
      if (!accountID || !product || !orderID || !selectedOrder) {
        return Promise.resolve(null);
      }

      return loadAtdReplacementChain({
        accountID,
        product,
        selectedOrder,
        fetchOrder: (referencedOrderID) =>
          queryClient.fetchQuery({
            queryKey: queryKeys.orders.detail(
              accountID,
              referencedOrderID,
              product,
            ),
            queryFn: () =>
              getAtdOrder({
                accountID,
                product,
                orderID: referencedOrderID,
              }),
            staleTime: DETAIL_CACHE_STALE_TIME_MS,
          }),
      });
    },
    {
      enabled:
        enabled && Boolean(accountID && product && orderID && selectedOrder),
      meta: { showErrorToast: false },
      retry: false,
    },
  );
};
